/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * analyticsModel
 * --------------
 * Pure functions behind /feed/analytics — a port of the UpVibe Android
 * Analytics screen, so the web and the app show the same numbers:
 *
 *   DateRangeSpec                  → rangeForFilter / customRange / drillIntoMonth / drillIntoWeek
 *   ActivityDao.get*Trend (SQL)    → bucketKey
 *   GetActivityAnalyticsUseCase    → buildAnalytics
 *   ActivityDao.getRangeTotals     → rangeTotals (headline, tiles, change badge)
 *   DateRangeSpec.previous         → previousRange
 *   ActivityDao.observeRecordedTypes → recordedTypes
 *   AnalyticsViewModel.toUiState   → chartSeries (+ AnalyticsFragment.bind's unit conversion)
 *   AnalyticsFragment headline/tiles → headlineValue / changeBadge / formatMoving / periodLabel
 *   AnalyticsViewModel drill stack → analyticsReducer
 *   AnalyticsBarChartView          → tickIndices
 *
 * Like the app, every bound and bucket is in UTC: the app stores started_at as
 * "yyyy-MM-ddTHH:mm:ssZ" and groups on substrings of it, so a bucket is a UTC
 * day / week-of-month / month on both platforms.
 */

import { TYPE_META, TYPE_ORDER, typeMeta } from '../feedModel';

export const DAILY = 'DAILY';
export const WEEKLY = 'WEEKLY';
export const MONTHLY = 'MONTHLY';

/**
 * The date-range segments, in the app's order (DateRangeFilter): `short` is the
 * segment text (range_short_*), `label` the full name (filter_*) used for the
 * accessible name and the headline's period.
 */
export const RANGE_FILTERS = [
  { id: 'LAST_30_DAYS', short: '30D', label: 'Last 30 Days' },
  { id: 'LAST_3_MONTHS', short: '3M', label: 'Last 3 Months' },
  { id: 'LAST_6_MONTHS', short: '6M', label: 'Last 6 Months' },
  { id: 'LAST_1_YEAR', short: '1Y', label: 'Last 1 Year' },
  { id: 'CUSTOM', short: 'Custom', label: 'Custom Range' },
];
export const DEFAULT_RANGE = 'LAST_30_DAYS';

/** Custom-range granularity thresholds (DateRangeSpec.CUSTOM_*_MAX_DAYS). */
export const CUSTOM_DAILY_MAX_DAYS = 31;
export const CUSTOM_WEEKLY_MAX_DAYS = 186;

/** AnalyticsFragment's km → mi factor. */
export const KM_TO_MI = 0.621371;

const DAY_MS = 24 * 60 * 60 * 1000;
const pad2 = (n) => String(n).padStart(2, '0');

/* ───────────────────────────── Date ranges ───────────────────────────── */

/** A Date → "yyyy-MM-ddTHH:mm:ssZ", truncated to whole seconds (DateRangeSpec.format). */
export function isoSeconds(date) {
  const d = new Date(Math.floor(date.getTime() / 1000) * 1000);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`
    + `T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}Z`;
}

const daysInMonth = (year, monthIndex) => new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

/**
 * java.time's ZonedDateTime.minusMonths in UTC: the day of month is clamped to
 * the target month's length (31 Mar − 1 month = 28/29 Feb), time kept.
 */
export function minusMonthsUtc(date, months) {
  const y = date.getUTCFullYear();
  const total = y * 12 + date.getUTCMonth() - months;
  const ty = Math.floor(total / 12);
  const tm = total - ty * 12;
  const day = Math.min(date.getUTCDate(), daysInMonth(ty, tm));
  return new Date(Date.UTC(ty, tm, day, date.getUTCHours(), date.getUTCMinutes(),
    date.getUTCSeconds(), date.getUTCMilliseconds()));
}

function spec(from, to, granularity) {
  return { fromIso: isoSeconds(from), toIso: isoSeconds(to), granularity };
}

/** DateRangeSpec.forFilter — the fixed chips, measured back from `now`. */
export function rangeForFilter(filter, now = new Date()) {
  switch (filter) {
    case 'LAST_30_DAYS':
      return spec(new Date(now.getTime() - 30 * DAY_MS), now, DAILY);
    case 'LAST_3_MONTHS':
      return spec(minusMonthsUtc(now, 3), now, WEEKLY);
    case 'LAST_6_MONTHS':
      // Monthly, not weekly: ~26 weekly points is too dense for an overview.
      return spec(minusMonthsUtc(now, 6), now, MONTHLY);
    case 'LAST_1_YEAR':
      return spec(minusMonthsUtc(now, 12), now, MONTHLY);
    default:
      throw new Error('rangeForFilter() does not accept CUSTOM — use customRange(fromMs, toMs)');
  }
}

/**
 * DateRangeSpec.custom — `fromMs`/`toMs` are UTC midnights of the picked days
 * (what the app's MaterialDatePicker returns). The end day is inclusive, so the
 * upper bound is one day past it; the span picks the granularity.
 */
export function customRange(fromMs, toMs) {
  const to = toMs + DAY_MS;
  const spanDays = Math.floor((to - fromMs) / DAY_MS);
  const granularity = spanDays <= CUSTOM_DAILY_MAX_DAYS ? DAILY
    : spanDays <= CUSTOM_WEEKLY_MAX_DAYS ? WEEKLY
      : MONTHLY;
  return spec(new Date(fromMs), new Date(to), granularity);
}

/** "yyyy-MM-dd" (a date input's value) → that day's UTC midnight in ms, or null. */
export function utcDayMs(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(ms) ? null : ms;
}

/** Drill from a MONTHLY bucket ("2026-07") into that whole calendar month's weeks. */
export function drillIntoMonth(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  return spec(new Date(Date.UTC(y, m - 1, 1)), new Date(Date.UTC(y, m, 1)), WEEKLY);
}

/**
 * Drill from a WEEKLY bucket ("2026-07-W5") into its days — that 7-day window,
 * clamped to the month's end for a trailing partial week (a week bucket never
 * holds days from the next month).
 */
export function drillIntoWeek(weekKey) {
  const idx = weekKey.indexOf('-W');
  const [y, m] = weekKey.slice(0, idx).split('-').map(Number);
  const week = Number(weekKey.slice(idx + 2));
  const monthEnd = Date.UTC(y, m, 1);
  const from = Date.UTC(y, m - 1, 1) + (week - 1) * 7 * DAY_MS;
  const to = Math.min(from + 7 * DAY_MS, monthEnd);
  return spec(new Date(from), new Date(to), DAILY);
}

/**
 * DateRangeSpec.previous — the equal-length window just before `range`, same
 * granularity: the comparison period for the headline's change badge (the
 * last 30 days → the 30 days before that).
 */
export function previousRange(range) {
  const from = Date.parse(range.fromIso);
  const to = Date.parse(range.toIso);
  return spec(new Date(from - (to - from)), new Date(from), range.granularity);
}

/** The next-finer range for a bucket, or null at DAILY (nothing finer to show). */
export function drillTarget(granularity, key) {
  if (granularity === MONTHLY) return drillIntoMonth(key);
  if (granularity === WEEKLY) return drillIntoWeek(key);
  return null;
}

/* ───────────────────────────── Buckets ───────────────────────────── */

/**
 * The SQL bucket key for one activity start (ActivityDao):
 *   DAILY   substr(started_at, 1, 10)                       "2026-08-03"
 *   WEEKLY  strftime('%Y-%m') || '-W' || ((day - 1) / 7 + 1) "2026-08-W1"  (week of MONTH)
 *   MONTHLY strftime('%Y-%m')                                "2026-08"
 */
export function bucketKey(date, granularity) {
  const ym = `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}`;
  if (granularity === DAILY) return `${ym}-${pad2(date.getUTCDate())}`;
  if (granularity === WEEKLY) return `${ym}-W${Math.floor((date.getUTCDate() - 1) / 7) + 1}`;
  return ym;
}

function monthName(year, monthIndex, locale) {
  return new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, monthIndex, 1)));
}

/**
 * Bucket key → display label (GetActivityAnalyticsUseCase.formatBucketLabel):
 * "Aug 3", "Aug - W1", "Aug". Month names follow the viewer's locale, as the app
 * follows the device's; anything unparseable is shown raw rather than thrown.
 */
export function bucketLabel(key, granularity, locale) {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2})|-W(\d))?$/.exec(String(key));
  if (!m) return String(key);
  const month = monthName(Number(m[1]), Number(m[2]) - 1, locale);
  if (granularity === DAILY && m[3]) return `${month} ${Number(m[3])}`;
  if (granularity === WEEKLY && m[4]) return `${month} - W${m[4]}`;
  if (granularity === MONTHLY && !m[3] && !m[4]) return month;
  return String(key);
}

/* ───────────────────────────── Aggregation ───────────────────────────── */

const inRange = (a, fromMs, toMs) => {
  // started_at is compared as whole-second text in the app.
  const t = Math.floor(a.startedAt.getTime() / 1000) * 1000;
  return t >= fromMs && t < toMs;
};

/**
 * GetActivityAnalyticsUseCase.execute over normalized activities (feedModel):
 *  - trend: one point per bucket that has activity, oldest first, narrowed to
 *    `type` when one is picked; distance in km and duration in minutes;
 *  - breakdown: always counted over EVERY type so "Run: 6%" means 6% of all
 *    activities in the range, then narrowed to `type`'s own row when picked;
 *    types the app does not know are left out of the rows but still counted in
 *    the total, as the app's safeValueOf skip does.
 */
export function buildAnalytics(activities, range, type = null, locale) {
  const fromMs = Date.parse(range.fromIso);
  const toMs = Date.parse(range.toIso);
  const within = (activities || []).filter((a) => a && a.startedAt && inRange(a, fromMs, toMs));

  const buckets = new Map();
  within.forEach((a) => {
    if (type && a.type !== type) return;
    const key = bucketKey(a.startedAt, range.granularity);
    const b = buckets.get(key) || { meters: 0, seconds: 0, count: 0 };
    b.meters += (a.distanceKm || 0) * 1000;
    b.seconds += a.durationSec || 0;
    b.count += 1;
    buckets.set(key, b);
  });
  const trendPoints = [...buckets.keys()].sort().map((key) => {
    const b = buckets.get(key);
    return {
      key,
      label: bucketLabel(key, range.granularity, locale),
      distanceKm: b.meters / 1000,
      durationMinutes: b.seconds / 60,
      count: b.count,
    };
  });

  const counts = new Map();
  within.forEach((a) => counts.set(a.type, (counts.get(a.type) || 0) + 1));
  const totalCount = within.length;
  const breakdown = [...counts]
    .filter(([t]) => TYPE_META[t])
    .filter(([t]) => !type || t === type)
    .map(([t, count]) => ({ type: t, count, pct: totalCount > 0 ? Math.round((100 * count) / totalCount) : 0 }))
    // Most first; ties in the order SQLite's GROUP BY returns them (type name).
    .sort((x, y) => y.count - x.count || (x.type < y.type ? -1 : x.type > y.type ? 1 : 0));

  return { trendPoints, breakdown, totalCount };
}

/**
 * ActivityDao.getRangeTotals: count, distance, moving time and calories over
 * the range, narrowed to `type` when one is picked (unlike the breakdown).
 */
export function rangeTotals(activities, range, type = null) {
  const fromMs = Date.parse(range.fromIso);
  const toMs = Date.parse(range.toIso);
  const totals = { count: 0, distanceKm: 0, movingSeconds: 0, calories: 0 };
  (activities || []).forEach((a) => {
    if (!a || !a.startedAt || !inRange(a, fromMs, toMs) || (type && a.type !== type)) return;
    totals.count += 1;
    totals.distanceKm += a.distanceKm || 0;
    totals.movingSeconds += a.durationSec || 0;
    totals.calories += a.kcal || 0;
  });
  totals.calories = Math.round(totals.calories);
  return totals;
}

/**
 * The types the athlete has recorded, in the app's chip order — one chip
 * each, as observeRecordedTypes feeds the app. Takes any number of activity
 * lists; types the app does not know are left out.
 */
export function recordedTypes(...lists) {
  const seen = new Set();
  lists.forEach((list) => (list || []).forEach((a) => { if (a && a.type) seen.add(a.type); }));
  return TYPE_ORDER.filter((t) => seen.has(t));
}

/** False only for types that record no distance (Yoga) — ActivityType.supportsDistance. */
export function supportsDistance(type) {
  return typeMeta(type).measure !== 'none';
}

/** user_profile.preferred_unit → 'MILES' | 'KM' (the app's UnitPreference, KM by default). */
export function unitPreference(profileRow) {
  return String(profileRow?.preferred_unit || '').toUpperCase() === 'MILES' ? 'MILES' : 'KM';
}

/**
 * AnalyticsViewModel.toUiState + AnalyticsFragment.bind: which metric the chart
 * plots and in what unit.
 *  - a no-distance type (Yoga) plots duration in minutes — distance is always 0;
 *  - Swim plots metres, as swim goals are set and shown in metres;
 *  - everything else plots km, or miles when that is the athlete's unit.
 */
export function chartSeries(analytics, { type = null, unit = 'KM' }) {
  const useDuration = !!type && !supportsDistance(type);
  let unitLabel;
  let factor = 1;
  if (useDuration) unitLabel = 'min';
  else if (type === 'swim') { unitLabel = 'm'; factor = 1000; }
  else if (unit === 'MILES') { unitLabel = 'mi'; factor = KM_TO_MI; }
  else unitLabel = 'km';

  const points = analytics.trendPoints.map((p) => ({
    key: p.key,
    label: p.label,
    value: (useDuration ? p.durationMinutes : p.distanceKm) * factor,
  }));
  return {
    points,
    unit: unitLabel,
    factor,
    metric: useDuration ? 'Duration' : 'Distance',
    useDuration,
  };
}

/* ───────────────────────────── Headline & tiles ───────────────────────────── */

/** "1h 5m", or "45m" under an hour (duration_h_m / duration_m). */
export function formatMoving(seconds) {
  const minutes = Math.floor((seconds || 0) / 60);
  return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`;
}

/**
 * The headline number and its unit (AnalyticsFragment.bindHeadline): duration
 * reads "1h 5m" with no unit from an hour up, else whole minutes + "min";
 * distance has one decimal, or none (with grouping) from 100 up and for swims.
 */
export function headlineValue(value, { unit, useDuration, swim = false }, locale) {
  if (useDuration) {
    const minutes = Math.round(value || 0);
    return minutes >= 60
      ? { value: `${Math.floor(minutes / 60)}h ${minutes % 60}m`, unit: '' }
      : { value: String(minutes), unit };
  }
  const v = value || 0;
  return {
    value: v >= 100 || swim
      ? new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(v)
      : v.toFixed(1),
    unit,
  };
}

/**
 * The change vs the previous equal-length period (bindChangeBadge): null when
 * both are zero, "New" when there was nothing before, else "↑ 12%" / "↓ 5%".
 * `up` picks the accent pill (current ≥ previous).
 */
export function changeBadge(current, previous) {
  const cur = current || 0;
  const prev = previous || 0;
  if (cur <= 0 && prev <= 0) return null;
  if (prev <= 0) return { text: 'New', up: true };
  const pct = Math.round(((cur - prev) / prev) * 100);
  return { text: pct >= 0 ? `↑ ${pct}%` : `↓ ${Math.abs(pct)}%`, up: cur >= prev };
}

/** "Last 30 Days", or "3 Aug – 20 Sep" for a custom range (AnalyticsFragment.periodLabel). */
export function periodLabel(state, locale) {
  if (state.range === 'CUSTOM' && state.custom) {
    const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
    return `${fmt.format(new Date(state.custom.fromMs))} – ${fmt.format(new Date(state.custom.toMs))}`;
  }
  return (RANGE_FILTERS.find((r) => r.id === state.range) || RANGE_FILTERS[0]).label;
}

/** The hint under the chart, by what one bar is (analytics_hint_*). */
export const CHART_HINT = {
  [DAILY]: 'Tap or drag on the chart to see a day',
  [WEEKLY]: 'Tap or drag on the chart to see a week',
  [MONTHLY]: 'Tap or drag on the chart to see a month',
};

/* ───────────────────────────── Bar chart ───────────────────────────── */

const DESIRED_TICKS = 4;

/**
 * Which bars get an X label (AnalyticsBarChartView.tickIndices): evenly
 * strided, always the last one, dropping a tick that would crowd it.
 */
export function tickIndices(n) {
  const ticks = [];
  if (n <= 0) return ticks;
  const stride = Math.max(1, Math.ceil(n / DESIRED_TICKS));
  for (let i = 0; i < n; i += stride) ticks.push(i);
  const last = n - 1;
  if (ticks[ticks.length - 1] !== last) {
    if (ticks.length > 1 && last - ticks[ticks.length - 1] < Math.floor(stride / 2) + 1) ticks.pop();
    ticks.push(last);
  }
  return ticks;
}

/** The bar drawn in accent: the selected one, else the latest bucket with a value. */
export function highlightIndex(points, selected) {
  if (selected >= 0) return selected;
  for (let i = points.length - 1; i >= 0; i -= 1) if (points[i].value > 0) return i;
  return -1;
}

/* ───────────────────────────── Screen state ───────────────────────────── */

/**
 * AnalyticsViewModel's state: the chosen chip, the type filter and the drill
 * stack. stack[0] is the top-level range; each drill pushes one finer level
 * with its bucket label for the breadcrumb; the last entry is what is shown.
 * Changing the range resets the stack (a drilled view of the old range would
 * dangle). `now` is resolved when the range is chosen, as load() does.
 */
export function initialAnalyticsState(now = new Date()) {
  return { range: DEFAULT_RANGE, custom: null, type: null, stack: [{ spec: rangeForFilter(DEFAULT_RANGE, now), label: null }] };
}

export function analyticsReducer(state, action) {
  const top = (s) => [{ spec: s, label: null }];
  switch (action.type) {
    case 'range':
      return { ...state, range: action.range, custom: null, stack: top(rangeForFilter(action.range, action.now || new Date())) };
    case 'custom':
      return { ...state, range: 'CUSTOM', custom: { fromMs: action.fromMs, toMs: action.toMs }, stack: top(customRange(action.fromMs, action.toMs)) };
    case 'activityType':
      // Same top-level range, back to the top level.
      return { ...state, type: action.activityType || null, stack: state.stack.slice(0, 1) };
    case 'drillInto': {
      const current = state.stack[state.stack.length - 1].spec;
      const child = drillTarget(current.granularity, action.key);
      if (!child) return state;
      return { ...state, stack: [...state.stack, { spec: child, label: action.label }] };
    }
    case 'drillUp':
      return state.stack.length <= 1 ? state : { ...state, stack: state.stack.slice(0, -1) };
    case 'drillUpTo': {
      // index is a breadcrumb position: 0 = first drilled level (stack[1]).
      const size = action.index + 2;
      return size >= state.stack.length ? state : { ...state, stack: state.stack.slice(0, size) };
    }
    default:
      return state;
  }
}

/** The breadcrumb labels below the top level (empty at the top). */
export const breadcrumbOf = (state) => state.stack.slice(1).map((e) => e.label);

/** The range currently queried and drawn. */
export const currentSpec = (state) => state.stack[state.stack.length - 1].spec;

/** Every type, in the app's order (the chips show only the recorded ones). */
export const ANALYTICS_TYPES = TYPE_ORDER;
